import { StrictMode, useMemo } from 'react'
import ReactDOM from 'react-dom/client'
import { AxiosError } from 'axios'
import * as Sentry from '@sentry/react'
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/auth-store'
import { handleServerError } from '@/lib/handle-server-error'
import { initSentry } from '@/lib/sentry'
import { LoadingSpinner } from '@/components/loading-spinner'
import { SentryUserSync } from '@/components/sentry-user-sync'
import { ClerkWrapper, useClerkAuth } from './auth/clerk'
import { DirectionProvider } from './context/direction-provider'
import { FontProvider } from './context/font-provider'
import { ThemeProvider } from './context/theme-provider'
// Generated Routes
import { routeTree } from './routeTree.gen'
// Styles
import './styles/index.css'

// Initialize Sentry before any rendering so boot-time errors are captured.
initSentry()

// Enable Clerk network debugging in development
if (import.meta.env.DEV) {
  import('@/utils/clerk-debug').then(({ debugClerkNetwork }) => {
    debugClerkNetwork()
  })
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // eslint-disable-next-line no-console
        if (import.meta.env.DEV) console.log({ failureCount, error })

        if (failureCount >= 0 && import.meta.env.DEV) return false
        if (failureCount > 3 && import.meta.env.PROD) return false

        return !(
          error instanceof AxiosError &&
          [401, 403].includes(error.response?.status ?? 0)
        )
      },
      refetchOnWindowFocus: import.meta.env.PROD,
      staleTime: 10 * 1000, // 10s
    },
    mutations: {
      onError: (error) => {
        handleServerError(error)

        if (error instanceof AxiosError) {
          if (error.response?.status === 304) {
            toast.error('Content not modified!')
          }
        }
      },
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          toast.error('Session expired!')
          useAuthStore.getState().auth.reset()
          const redirect = window.location.href
          window.location.href = `/sign-in?redirect=${encodeURIComponent(redirect)}`
        }
        if (error.response?.status === 500) {
          toast.error('Internal Server Error!')
          window.location.href = '/500'
        }
        if (error.response?.status === 403) {
          // Handle 403 if needed
        }
      }
    },
  }),
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createRouter>
  }
}

function InnerApp() {
  const auth = useClerkAuth()

  // Memoize router creation to prevent unnecessary re-creation
  // Hooks must be called before any conditional returns
  const router = useMemo(
    () =>
      createRouter({
        routeTree,
        context: { queryClient, auth },
        defaultPreload: 'intent',
        defaultPreloadStaleTime: 0,
      }),
    [auth]
  )

  const routerContext = useMemo(() => ({ queryClient, auth }), [auth])

  if (auth.isLoading) {
    return (
      <div className='flex min-h-screen items-center justify-center'>
        <LoadingSpinner message="Loading…" size="lg" />
      </div>
    )
  }

  return <RouterProvider router={router} context={routerContext} />
}

// Render the app
const rootElement = document.getElementById('root')!
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <Sentry.ErrorBoundary
        fallback={
          <div className='flex min-h-screen items-center justify-center p-8 text-center'>
            <div>
              <h1 className='mb-2 text-xl font-semibold'>Something went wrong</h1>
              <p className='text-muted-foreground text-sm'>
                The error has been reported. Try refreshing the page.
              </p>
            </div>
          </div>
        }
      >
        <ClerkWrapper>
          <SentryUserSync />
          <QueryClientProvider client={queryClient}>
            <ThemeProvider>
              <FontProvider>
                <DirectionProvider>
                  <InnerApp />
                </DirectionProvider>
              </FontProvider>
            </ThemeProvider>
          </QueryClientProvider>
        </ClerkWrapper>
      </Sentry.ErrorBoundary>
    </StrictMode>
  )
}
